import React, { useState, useEffect, useCallback } from "react";
import {
  getProducts,
  getProductVariants,
  upsertVariant,
  updateVariantStock,
  deleteVariant,
  getReturnedOrders,
  restockOrder,
} from "../services/api";
import {
  Package,
  Search,
  Loader2,
  Plus,
  Trash2,
  Edit3,
  X,
  Check,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Palette,
  Maximize,
  TrendingUp,
  TrendingDown,
  Archive,
  RotateCcw,
  Truck,
  History,
  Calendar,
} from "lucide-react";
import { formatSizeMapping, formatOrderVariant } from "../utils/variantUtils";

const StockManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [variants, setVariants] = useState({}); // { productId: [variant, ...] }
  const [toasts, setToasts] = useState([]);
  const [returnedOrders, setReturnedOrders] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [restockingId, setRestockingId] = useState(null);
  const [isReturnsExpanded, setIsReturnsExpanded] = useState(false);

  // Variant form state
  const [variantModal, setVariantModal] = useState(null); // { productId, variant? }
  const [variantForm, setVariantForm] = useState({
    color: "",
    size: "",
    quantity: "0",
  });
  const [submitting, setSubmitting] = useState(false);

  // Inline stock edit
  const [editingStock, setEditingStock] = useState({}); // { variantId: newQuantityValue }

  const showToast = useCallback((type, message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      3000,
    );
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProducts({ limit: 200 });
      setProducts(Array.isArray(res) ? res : res.data || []);
    } catch {
      showToast("error", "Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchReturnedOrders = useCallback(async () => {
    try {
      setLoadingReturns(true);
      const res = await getReturnedOrders();
      setReturnedOrders(res.data || []);
    } catch {
      // Sulent error for returns fetch
    } finally {
      setLoadingReturns(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchReturnedOrders();
  }, [fetchProducts, fetchReturnedOrders]);

  // Variant fetch is no longer needed on click, as variants are returned with products
  const toggleProduct = (productId) => {
    setExpandedProductId((prev) => (prev === productId ? null : productId));
  };

  const openAddVariant = (productId) => {
    setVariantForm({ color: "", size: "", quantity: "0" });
    setVariantModal({ productId });
  };

  const openEditVariant = (productId, variant) => {
    setVariantForm({
      color: variant.color,
      size: variant.size,
      quantity: variant.quantity ?? 0,
    });
    setVariantModal({ productId, variant });
  };

  const handleVariantSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await upsertVariant(variantModal.productId, {
        color: variantForm.color.trim(),
        size: variantForm.size.trim(),
        quantity: Number(variantForm.quantity) || 0,
        variantId: variantModal.variant?._id, // PASSING ID FOR UPDATES
      });
      setVariants((prev) => ({ ...prev, [variantModal.productId]: res.data }));
      showToast(
        "success",
        variantModal.variant ? "Variante mise à jour ✓" : "Variante ajoutée ✓",
      );
      setVariantModal(null);
    } catch (err) {
      showToast(
        "error",
        err.response?.data?.message || "Erreur lors de la sauvegarde",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVariant = async (productId, variantId) => {
    if (!window.confirm("Supprimer cette variante ?")) return;
    try {
      const res = await deleteVariant(productId, variantId);
      setVariants((prev) => ({ ...prev, [productId]: res.data }));
      showToast("success", "Variante supprimée");
    } catch {
      showToast("error", "Erreur lors de la suppression");
    }
  };

  const handleInlineStockSave = async (productId, variantId) => {
    const newQuantity = editingStock[variantId];
    if (newQuantity === undefined) return;
    try {
      const res = await updateVariantStock(productId, variantId, newQuantity);
      setVariants((prev) => ({ ...prev, [productId]: res.data }));
      setEditingStock((prev) => {
        const c = { ...prev };
        delete c[variantId];
        return c;
      });
      showToast("success", "Stock mis à jour ✓");
    } catch {
      showToast("error", "Erreur lors de la mise à jour du stock");
    }
  };

  const handleRestock = async (orderId) => {
    try {
      setRestockingId(orderId);
      await restockOrder(orderId);
      showToast("success", "Produits réintégrés au stock ✓");
      fetchReturnedOrders();
      fetchProducts(); // Refresh products to see updated stock
    } catch (err) {
      showToast("error", err.response?.data?.message || "Erreur lors de la réintégration");
    } finally {
      setRestockingId(null);
    }
  };

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getStockColor = (stock) => {
    if (stock <= 0) return "text-red-700 bg-red-50 border-red-100";
    if (stock <= 5) return "text-amber-700 bg-amber-50 border-amber-100";
    return "text-emerald-700 bg-emerald-50 border-emerald-100";
  };

  const getStockIcon = (stock) => {
    if (stock <= 0) return <TrendingDown size={12} />;
    if (stock <= 5) return <Archive size={12} />;
    return <TrendingUp size={12} />;
  };

  return (
    <div className="px-4 py-6 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
            Gestion du Stock
          </h1>
          <p className="text-xs md:text-sm text-gray-500 font-medium mt-1 uppercase tracking-widest">
            Gérez vos variantes (Couleur + Taille) et niveaux de stock
          </p>
        </div>
        <div className="flex items-center gap-3">
          {returnedOrders.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 border border-rose-100 rounded-full text-[10px] font-black uppercase tracking-widest text-rose-600 animate-pulse">
              <RotateCcw size={12} />
              {returnedOrders.length} RETOURS À TRAITER
            </div>
          )}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-full text-[10px] font-black uppercase tracking-widest text-indigo-600">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
            {products.length} Produits au total
          </div>
        </div>
      </div>

      {/* Returned Orders Section */}
      <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-500">
        <button 
          onClick={() => setIsReturnsExpanded(!isReturnsExpanded)}
          className="w-full flex items-center justify-between gap-3 mb-4 px-3 py-3 bg-white border border-gray-100 rounded-3xl hover:bg-gray-50 transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white shadow-lg transition-all ${returnedOrders.length > 0 ? 'bg-rose-500 shadow-rose-200' : 'bg-gray-400 shadow-gray-100'}`}>
              <RotateCcw size={20} className={returnedOrders.length > 0 ? 'animate-spin-slow' : ''} />
            </div>
            <div className="text-left">
              <h2 className="text-sm font-black text-gray-900 tracking-tight">Suivi des Retours & Refus</h2>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                {returnedOrders.length > 0 ? `${returnedOrders.length} article(s) à réintégrer` : 'Aucun retour en attente'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
             {returnedOrders.length > 0 && (
                <span className="px-2.5 py-1 bg-rose-500 text-white text-[10px] font-black rounded-lg">
                  IMPORTANT
                </span>
             )}
             <div className="w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-indigo-50 transition-colors">
               {isReturnsExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
             </div>
          </div>
        </button>
        
        {isReturnsExpanded && (
          <div className="bg-white rounded-[2rem] border-2 border-gray-50 shadow-xl shadow-gray-100/50 overflow-hidden animate-in zoom-in-95 duration-200">
            {returnedOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center">
                   <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                     Affichage de tous les retours (depuis le 20 Avril)
                   </p>
                </div>
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">ID Commande</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Produit</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Variante</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Client</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Transporteur</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Statut / Raison</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {returnedOrders.map((order) => (
                      <tr key={order._id} className="hover:bg-gray-50/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-extrabold text-gray-900 text-[13px]">{order.orderId}</span>
                            {(() => {
                              // Find the first occurrence of a terminal status in history for better date accuracy
                              let d = order.delivery?.lastCarrierEventAt || order.delivery?.statusUpdatedAt;
                              
                              if (order.delivery?.statusHistory?.length > 0) {
                                const terminalStatuses = ['CANCELLED', 'RETURNED', 'REFUSED', 'REJECTED'];
                                const firstTerminalEvent = order.delivery.statusHistory.find(h => 
                                  terminalStatuses.includes(h.status?.toUpperCase()) || 
                                  (h.label && (h.label.toLowerCase().includes('refus') || h.label.toLowerCase().includes('reject') || h.label.toLowerCase().includes('cancel')))
                                );
                                if (firstTerminalEvent?.at) {
                                  d = firstTerminalEvent.at;
                                }
                              }

                              if (!d) return null;
                              const date = new Date(d);
                              return (
                                <span className="flex items-center gap-1 mt-0.5">
                                  <Calendar size={10} className="text-gray-300" />
                                  <span className="text-[10px] font-medium text-gray-400">
                                    {date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </span>
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0">
                              {order.productId?.image ? (
                                <img src={order.productId.image} className="w-full h-full object-cover" />
                              ) : (
                                <Package size={14} className="m-2 text-gray-300" />
                              )}
                             </div>
                             <span className="font-bold text-gray-700 text-[13px]">{order.productId?.name || order.productName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <Palette size={12} className="text-rose-400" />
                              <span className="px-2 py-0.5 bg-gray-100 rounded-md text-[11px] font-bold text-gray-600">
                                {formatOrderVariant(order)}
                              </span>
                            </div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider pl-0.5">
                              x{order.quantity || 1}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-bold text-gray-800 text-[13px]">{order.shipping?.receiverName || order.name}</span>
                            <span className="text-[10px] text-gray-400 font-medium">{order.shipping?.phone || order.phone}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                             <Truck size={12} className="text-indigo-400" />
                             <span className="text-[11px] font-black uppercase tracking-wider text-gray-600">
                               {order.delivery?.provider?.name || order.delivery?.providerSlug || "N/A"}
                             </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${order.status === 'refused' ? 'bg-orange-50 text-orange-600 border border-orange-100' : order.status === 'cancelled' ? 'bg-amber-50 text-amber-600 border border-amber-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                            {order.status === 'refused' ? 'Refusé' : order.status === 'cancelled' ? 'Annulé' : 'Retourné'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleRestock(order._id)}
                            disabled={restockingId === order._id}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-rose-600 text-white rounded-xl text-[11px] font-black uppercase tracking-widest transition-all hover:scale-105 active:scale-95 disabled:opacity-50 shadow-lg shadow-gray-200"
                          >
                            {restockingId === order._id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <RotateCcw size={14} />
                            )}
                            Restocker
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500">
                   <CheckCircle size={32} />
                </div>
                <div className="text-center">
                   <p className="text-sm font-black text-gray-900">Tout est en ordre !</p>
                   <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest">Aucun retour à traiter pour le moment.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden mb-4">
        <div className="p-4 md:p-5 border-b border-gray-50 bg-gray-50/30 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Rechercher par nom de produit..."
              className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-sm transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Product list */}
        <div className="divide-y divide-gray-50">
          {loading ? (
            <div className="p-16 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
              <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">
                Chargement des produits...
              </p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package className="text-gray-300" size={32} />
              </div>
              <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">
                Aucun produit trouvé
              </p>
            </div>
          ) : (
            filteredProducts.map((product) => {
              const isExpanded = expandedProductId === product._id;
              const productVariants =
                variants[product._id] || product.variants || [];
              const totalStock = productVariants.reduce(
                (s, v) => s + (v.quantity || 0),
                0,
              );

              return (
                <div
                  key={product._id}
                  className="overflow-hidden border border-gray-100/50 rounded-2xl mb-4 bg-white shadow-sm"
                >
                  {/* Product Row */}
                  <button
                    onClick={() => toggleProduct(product._id)}
                    className="w-full flex items-center gap-4 px-5 md:px-6 py-4 hover:bg-gray-50/60 transition-colors text-left group"
                  >
                    <div className="w-12 h-12 rounded-2xl overflow-hidden flex-shrink-0 bg-indigo-50 flex items-center justify-center border border-gray-100 shadow-sm transition-transform">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <Package size={20} className="text-indigo-400" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-extrabold text-gray-900 text-[15px] truncate">
                        {product.name}
                      </p>
                      <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider mt-0.5">
                        {productVariants.length > 0
                          ? `${productVariants.length} VARIANTES CONFIGURÉES`
                          : "AUCUNE VARIANTE"}
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      {productVariants.length > 0 && (
                        <div
                          className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border ${getStockColor(totalStock)} shadow-sm`}
                        >
                          {getStockIcon(totalStock)}
                          {totalStock} UNITÉS
                        </div>
                      )}
                      <div className="text-gray-300 group-hover:text-indigo-400 transition-colors flex-shrink-0">
                        {isExpanded ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Variant Panel */}
                  {isExpanded && (
                  <div className="bg-gray-50/40 border-t border-gray-100 px-5 md:px-6 py-5">
                    <div className="space-y-4">
                      {productVariants.length > 0 ? (
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="bg-gray-50/50">
                                <th className="px-5 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                  Couleur
                                </th>
                                <th className="px-5 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                  Taille
                                </th>
                                <th className="px-5 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                  Stock Disponible
                                </th>
                                <th className="px-5 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {productVariants.map((variant) => (
                                <tr
                                  key={variant._id}
                                  className="hover:bg-gray-50/30 transition-colors"
                                >
                                  <td className="px-5 py-4">
                                    <div className="flex items-center gap-2.5">
                                      <Palette
                                        size={14}
                                        className="text-indigo-400"
                                      />
                                      <span className="font-extrabold text-gray-700 text-[13px]">
                                        {variant.color}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-5 py-4">
                                    <div className="flex items-center gap-2.5">
                                      <Maximize
                                        size={14}
                                        className="text-gray-400"
                                      />
                                      <span className="font-bold text-gray-600 text-[13px]">
                                        {formatSizeMapping(variant.size)}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-5 py-4">
                                    {editingStock[variant._id] !== undefined ? (
                                      <div className="flex items-center gap-2 animate-in zoom-in-95 duration-200">
                                        <input
                                          type="number"
                                          min="0"
                                          className="w-24 px-3 py-1.5 bg-white border-2 border-indigo-200 rounded-xl text-sm font-black focus:outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                          value={editingStock[variant._id]}
                                          onChange={(e) =>
                                            setEditingStock((prev) => ({
                                              ...prev,
                                              [variant._id]: e.target.value,
                                            }))
                                          }
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter")
                                              handleInlineStockSave(
                                                product._id,
                                                variant._id,
                                              );
                                            if (e.key === "Escape")
                                              setEditingStock((prev) => {
                                                const c = { ...prev };
                                                delete c[variant._id];
                                                return c;
                                              });
                                          }}
                                          autoFocus
                                        />
                                        <button
                                          onClick={() =>
                                            handleInlineStockSave(
                                              product._id,
                                              variant._id,
                                            )
                                          }
                                          className="p-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-xl transition-all"
                                        >
                                          <Check size={16} />
                                        </button>
                                        <button
                                          onClick={() =>
                                            setEditingStock((prev) => {
                                              const c = { ...prev };
                                              delete c[variant._id];
                                              return c;
                                            })
                                          }
                                          className="p-1.5 bg-gray-100 text-gray-500 hover:bg-gray-200 rounded-xl transition-all"
                                        >
                                          <X size={16} />
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() =>
                                          setEditingStock((prev) => ({
                                            ...prev,
                                            [variant._id]:
                                              variant.quantity ?? 0,
                                          }))
                                        }
                                        className={`group inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 text-[11px] font-black uppercase tracking-widest transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-sm ${getStockColor(variant.quantity ?? 0)}`}
                                        title="Modifier le stock"
                                      >
                                        {getStockIcon(variant.quantity ?? 0)}
                                        {variant.quantity ?? 0} UNITÉS
                                        <Edit3
                                          size={12}
                                          className="opacity-0 group-hover:opacity-40 transition-opacity ml-1"
                                        />
                                      </button>
                                    )}
                                  </td>
                                  <td className="px-5 py-4">
                                    <div className="flex justify-end gap-2">
                                      <button
                                        onClick={() =>
                                          openEditVariant(product._id, variant)
                                        }
                                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 rounded-xl transition-all"
                                        title="Modifier"
                                      >
                                        <Edit3 size={16} />
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleDeleteVariant(
                                            product._id,
                                            variant._id,
                                          )
                                        }
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-xl transition-all"
                                        title="Supprimer"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="py-12 text-center bg-white rounded-3xl border-2 border-dashed border-gray-100 italic">
                          <Archive
                            size={32}
                            className="text-gray-200 mx-auto mb-3"
                          />
                          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">
                            Aucune variante configurée pour ce produit
                          </p>
                        </div>
                      )}

                      <button
                        onClick={() => openAddVariant(product._id)}
                        className="w-full py-4 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-2xl font-black text-xs transition-all uppercase tracking-widest flex items-center justify-center gap-3 border-2 border-dashed border-indigo-200 hover:border-indigo-600"
                      >
                        <Plus size={18} />
                        Ajouter une nouvelle combinaison couleur/taille
                      </button>
                    </div>
                  </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Variant Modal */}
      {variantModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-7 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
              <div>
                <h2 className="text-xl font-black text-gray-900 tracking-tight">
                  {variantModal.variant
                    ? "Mise à jour Variante"
                    : "Nouvelle Variante"}
                </h2>
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-1">
                  {products.find((p) => p._id === variantModal.productId)?.name}
                </p>
              </div>
              <button
                onClick={() => setVariantModal(null)}
                className="p-2.5 hover:bg-red-50 hover:text-red-500 rounded-full transition-all group"
              >
                <X
                  size={20}
                  className="text-gray-300 group-hover:text-red-500"
                />
              </button>
            </div>
            <form onSubmit={handleVariantSubmit} className="p-7 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block px-1">
                    Couleur <span className="text-red-400 ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <Palette
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"
                      size={16}
                    />
                    <input
                      required
                      type="text"
                      className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl focus:outline-none font-bold text-sm transition-all"
                      value={variantForm.color}
                      onChange={(e) =>
                        setVariantForm((f) => ({ ...f, color: e.target.value }))
                      }
                      placeholder="ex: Rouge, Noir..."
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block px-1">
                    Taille <span className="text-red-400 ml-0.5">*</span>
                  </label>
                  <div className="relative">
                    <Maximize
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"
                      size={16}
                    />
                    <input
                      required
                      type="text"
                      className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl focus:outline-none font-bold text-sm transition-all"
                      value={variantForm.size}
                      onChange={(e) =>
                        setVariantForm((f) => ({ ...f, size: e.target.value }))
                      }
                      placeholder="ex: XL, 42, M..."
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block px-1">
                  Stock Initial
                </label>
                <input
                  required
                  type="number"
                  min="0"
                  className="w-full px-5 py-3.5 bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-2xl focus:outline-none font-black text-sm transition-all"
                  value={variantForm.quantity}
                  onChange={(e) =>
                    setVariantForm((f) => ({ ...f, quantity: e.target.value }))
                  }
                  placeholder="0"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setVariantModal(null)}
                  className="flex-1 py-4 rounded-2xl font-black text-gray-500 hover:bg-gray-100 transition-all uppercase tracking-widest text-[11px]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-3 py-4 bg-gray-900 hover:bg-black text-white rounded-2xl font-black transition-all shadow-xl shadow-gray-200 flex items-center justify-center gap-3 uppercase tracking-widest text-[11px] disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <CheckCircle size={18} />
                  )}
                  {variantModal.variant
                    ? "Enregistrer les changements"
                    : "Créer la variante"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="fixed bottom-8 right-8 z-[110] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-3 px-7 py-4 rounded-3xl shadow-2xl border-2 text-[13px] font-black tracking-tight animate-in slide-in-from-right-full pointer-events-auto
                            ${toast.type === "success" ? "bg-white border-emerald-50 text-emerald-800" : "bg-white border-red-50 text-red-800"}`}
          >
            {toast.type === "success" ? (
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500" />
            )}
            {toast.message.toUpperCase()}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StockManagement;
